// src/pages/DonationSuccess.jsx
import React, { useEffect, useState, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import { logDonation } from '../services/googleSheetsService';

export default function DonationSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [donationData, setDonationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('PENDING');
  const [statusMessage, setStatusMessage] = useState('Waiting for payment confirmation...');
  const [pollCount, setPollCount] = useState(0);
  const [isLogged, setIsLogged] = useState(false);
  const [logError, setLogError] = useState(null);
  const [showPendingState, setShowPendingState] = useState(false);
  const [isBackgroundPolling, setIsBackgroundPolling] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [pollingComplete, setPollingComplete] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(5);
  const [sseConnected, setSseConnected] = useState(false);
  
  // Get all params
  const reference = searchParams.get('reference');
  const orderTrackingId = searchParams.get('OrderTrackingId');
  const orderMerchantReference = searchParams.get('OrderMerchantReference');
  const checkoutRequestID = searchParams.get('checkoutRequestID');
  const amount = searchParams.get('amount') || localStorage.getItem('donationAmount') || '0';
  
  // Payment type detection
  const isMpesa = checkoutRequestID?.startsWith('ws_CO_') || 
                  reference?.startsWith('ws_CO_') || 
                  orderTrackingId?.startsWith('ws_CO_');
  
  const isPesapal = orderTrackingId && !orderTrackingId.startsWith('ws_CO_');

  // Use refs
  const hasLoggedRef = useRef(false);
  const hasEmailedRef = useRef(false);
  const pollingActiveRef = useRef(true);
  const pollTimeoutRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const eventSourceRef = useRef(null);

  // Constants
  const MAX_POLL_ATTEMPTS = 60;
  const POLL_INTERVAL = 2000;
  const LOADING_DISPLAY_ATTEMPTS = 5;
  const REDIRECT_DELAY_SECONDS = 5;

  // Google Sheets URL
  const GOOGLE_SHEETS_URL = process.env.REACT_APP_GOOGLE_SHEETS_URL || 'https://script.google.com/macros/s/AKfycbyPPsLkPCsDgooSvQAHiUqB64JIAoDGI4EefvUugr4y7bzLH7FVo2uST6ZOp9TG3_fe/exec';

  // ============================================
  // REDIRECT COUNTDOWN
  // ============================================
  const startRedirectCountdown = (targetPath = '/get-involved') => {
    setRedirectCountdown(REDIRECT_DELAY_SECONDS);
    
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }
    
    countdownIntervalRef.current = setInterval(() => {
      setRedirectCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownIntervalRef.current);
          navigate(targetPath);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // ============================================
  // SEND EMAIL FUNCTION
  // ============================================
  const sendDonationEmails = async (donorData) => {
    try {
      if (!GOOGLE_SHEETS_URL) {
        console.warn('⚠️ Google Sheets URL not configured, skipping emails');
        return false;
      }

      console.log('📧 Sending donation emails...');
      console.log('📧 Donor Email:', donorData.email);
      console.log('📧 Donor Name:', donorData.fullName);

      await fetch(GOOGLE_SHEETS_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          endpoint: 'donation_email',
          payload: {
            donorName: donorData.fullName || 'Supporter',
            donorEmail: donorData.email || '',
            amount: donorData.amount || '0',
            reference: donorData.reference || 'N/A',
            paymentMethod: donorData.paymentMethod || 'PesaPal',
            donationType: donorData.donationType || 'one-time',
            transactionDate: donorData.transactionDate || new Date().toISOString(),
            adminEmail: 'rafffnixx@gmail.com'
          }
        })
      });

      console.log('✅ Donation emails sent successfully');
      setEmailSent(true);
      return true;
    } catch (error) {
      console.error('❌ Failed to send donation emails:', error);
      return false;
    }
  };

  // ============================================
  // HANDLE PAYMENT SUCCESS
  // ============================================
  const handlePaymentSuccess = async (data, trackingId, isFromSse = false) => {
    if (donationData?.status === 'COMPLETED') return;
    
    console.log('✅ Payment COMPLETED!' + (isFromSse ? ' (via SSE)' : ' (via Polling)'));
    setPaymentStatus('COMPLETED');
    setStatusMessage('✅ Payment confirmed successfully!');
    setIsBackgroundPolling(false);
    setLoading(false);
    pollingActiveRef.current = false;
    setPollingComplete(true);
    
    // Close SSE connection if exists
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    
    const donorData = JSON.parse(localStorage.getItem('donationData') || '{}');
    
    let receiptNumber;
    let paymentMethod;
    let paymentDate;
    
    if (isMpesa) {
      receiptNumber = data.mpesaReceiptNumber || data.MpesaReceiptNumber || trackingId;
      paymentMethod = 'M-PESA (STK Push)';
      paymentDate = data.payment_date || data.TransactionDate || new Date().toISOString();
    } else {
      receiptNumber = data.confirmation_code || data.merchantReference || trackingId;
      paymentMethod = data.payment_method || 'PesaPal';
      paymentDate = data.payment_date || new Date().toISOString();
    }
    
    const actualAmount = data.amount || amount || '0';
    
    // ✅ LOG TO DATABASE & GOOGLE SHEETS
    if (!hasLoggedRef.current) {
      try {
        const logResult = await logDonation({
          fullName: donorData.fullName || 'Anonymous',
          email: donorData.email || '',
          phone: donorData.phone || '',
          amount: parseFloat(actualAmount),
          paymentMethod: paymentMethod,
          reference: receiptNumber || trackingId,
          status: 'Completed',
          donationType: donorData.donationType || 'one-time',
          transactionDate: paymentDate
        });
        
        if (logResult.success) {
          setIsLogged(true);
          hasLoggedRef.current = true;
          console.log('✅ Donation logged successfully (Database + Sheets)');
        }
      } catch (logError) {
        console.error('❌ Failed to log donation:', logError);
        setLogError('Could not save donation record, but your payment is confirmed.');
      }
    }
    
    if (!hasEmailedRef.current && donorData.email) {
      try {
        await sendDonationEmails({
          fullName: donorData.fullName || 'Anonymous',
          email: donorData.email || '',
          phone: donorData.phone || '',
          amount: actualAmount,
          paymentMethod: paymentMethod,
          reference: receiptNumber || trackingId,
          donationType: donorData.donationType || 'one-time',
          transactionDate: paymentDate
        });
        hasEmailedRef.current = true;
      } catch (emailError) {
        console.error('❌ Failed to send email:', emailError);
      }
    }
    
    localStorage.removeItem('donationData');
    localStorage.removeItem('donationAmount');
    localStorage.removeItem('checkoutRequestID');
    
    setDonationData({
      reference: receiptNumber || trackingId,
      amount: actualAmount,
      status: 'COMPLETED',
      paymentMethod: paymentMethod,
      paymentDate: paymentDate,
      confirmationCode: receiptNumber || trackingId
    });

    startRedirectCountdown('/get-involved');
  };

  // ============================================
  // SSE (SERVER-SENT EVENTS) CONNECTION
  // ============================================
  useEffect(() => {
    const trackingId = checkoutRequestID || reference || orderTrackingId || '';
    if (!trackingId || !isMpesa) return;

    console.log(`📡 Connecting to SSE stream for: ${trackingId}`);
    
    const apiUrl = process.env.REACT_APP_API_URL || 'https://kajiadobrighthorizons.org/api';
    const sseUrl = `${apiUrl}/daraja/status-stream/${trackingId}`;
    
    try {
      const eventSource = new EventSource(sseUrl);
      eventSourceRef.current = eventSource;
      
      eventSource.onopen = () => {
        console.log('📡 SSE connection opened');
        setSseConnected(true);
      };
      
      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('📡 SSE message received:', data);
          
          if (data.type === 'status_update' && data.data) {
            const statusData = data.data;
            
            if (statusData.isCompleted === true) {
              handlePaymentSuccess(statusData, trackingId, true);
            } else if (statusData.isFailed === true) {
              console.log('❌ Payment FAILED via SSE');
              setPaymentStatus('FAILED');
              setStatusMessage('❌ Payment was not completed');
              setIsBackgroundPolling(false);
              setLoading(false);
              pollingActiveRef.current = false;
              setPollingComplete(true);
              
              if (eventSourceRef.current) {
                eventSourceRef.current.close();
                eventSourceRef.current = null;
              }
              
              setDonationData({
                reference: trackingId,
                amount: amount,
                status: 'FAILED',
                message: statusData.resultDesc || 'Payment was not completed. Please try again.'
              });
              
              startRedirectCountdown('/donate');
            }
          }
        } catch (error) {
          console.error('❌ SSE parse error:', error);
        }
      };
      
      eventSource.onerror = (error) => {
        console.log('⚠️ SSE connection error, falling back to polling:', error);
        setSseConnected(false);
        eventSource.close();
        eventSourceRef.current = null;
      };
      
      return () => {
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        }
      };
    } catch (error) {
      console.error('❌ SSE connection error:', error);
    }
  }, [checkoutRequestID, reference, orderTrackingId]);

  // ============================================
  // MPESA POLLING LOGIC (Fallback)
  // ============================================
  const pollMpesaStatus = async (trackingId, attempt = 0) => {
    const apiUrl = process.env.REACT_APP_API_URL || 'https://kajiadobrighthorizons.org/api';
    const statusUrl = `${apiUrl}/daraja/status/${trackingId}`;
    
    try {
      console.log(`📊 M-PESA Poll attempt ${attempt + 1}/${MAX_POLL_ATTEMPTS}`);
      console.log(`📡 Fetching: ${statusUrl}`);
      
      const response = await fetch(statusUrl);
      const data = await response.json();
      console.log(`📥 M-PESA Response:`, data);

      const isCompleted = data.isCompleted === true || 
                         data.status === 'COMPLETED' ||
                         data.ResultCode === '0' ||
                         data.mpesaReceiptNumber ||
                         data.MpesaReceiptNumber;

      if (isCompleted) {
        console.log('✅ M-PESA Payment COMPLETED!');
        return { completed: true, data };
      }

      if (data.isFailed === true || data.status === 'FAILED' || data.status === 'CANCELLED') {
        console.log('❌ M-PESA Payment FAILED');
        return { completed: false, failed: true, data };
      }

      return { completed: false, pending: true, data };
    } catch (error) {
      console.error('❌ M-PESA Polling error:', error);
      return { completed: false, pending: true, data: null, error: error.message };
    }
  };

  // ============================================
  // PESAPAL POLLING LOGIC
  // ============================================
  const pollPesapalStatus = async (trackingId, attempt = 0) => {
    const apiUrl = process.env.REACT_APP_API_URL || 'https://kajiadobrighthorizons.org/api';
    const statusUrl = `${apiUrl}/payment/status?orderTrackingId=${trackingId}`;
    
    try {
      console.log(`📊 Pesapal Poll attempt ${attempt + 1}/${MAX_POLL_ATTEMPTS}`);
      console.log(`📡 Fetching: ${statusUrl}`);
      
      const response = await fetch(statusUrl);
      const data = await response.json();
      console.log(`📥 Pesapal Response:`, data);

      const isCompleted = data.isCompleted === true || 
                         data.status === '200' ||
                         data.status === 'COMPLETED' ||
                         data.payment_status_description === 'Completed' ||
                         data.confirmation_code;

      if (isCompleted) {
        console.log('✅ Pesapal Payment COMPLETED!');
        return { completed: true, data };
      }

      if (data.isFailed === true || data.status === 'FAILED' || data.status === 'CANCELLED') {
        console.log('❌ Pesapal Payment FAILED');
        return { completed: false, failed: true, data };
      }

      return { completed: false, pending: true, data };
    } catch (error) {
      console.error('❌ Pesapal Polling error:', error);
      return { completed: false, pending: true, data: null, error: error.message };
    }
  };

  // ============================================
  // MAIN POLLING LOGIC (Fallback when SSE fails)
  // ============================================
  useEffect(() => {
    if (donationData?.status === 'COMPLETED' || pollingComplete) {
      return;
    }

    if (isMpesa && sseConnected) {
      console.log('📡 SSE connected, skipping polling for M-PESA');
      return;
    }

    let trackingId = '';
    if (isMpesa) {
      trackingId = checkoutRequestID || reference || orderTrackingId || '';
    } else if (isPesapal) {
      trackingId = orderTrackingId || reference || '';
    }
    
    if (!trackingId) {
      console.error('❌ No transaction reference found!');
      setError('No transaction reference found');
      setLoading(false);
      return;
    }

    console.log(`🔍 Starting polling for: ${trackingId} (SSE fallback)`);
    console.log(`📱 Payment type: ${isMpesa ? 'M-PESA' : 'Pesapal'}`);
    
    let attempt = 0;
    let isPolling = true;

    const pollStatus = async () => {
      if (!isPolling || pollingComplete || donationData?.status === 'COMPLETED') {
        return;
      }

      attempt++;
      setPollCount(attempt);

      if (attempt === 1) {
        setStatusMessage('🔍 Checking payment status...');
      } else if (attempt === 2) {
        setStatusMessage('⏳ Payment is being processed...');
      } else if (attempt === 3) {
        setStatusMessage('⏳ Still processing, this may take a moment...');
      } else if (attempt > 3 && attempt <= 10) {
        setStatusMessage('⏳ Waiting for payment confirmation...');
      } else if (attempt > 10) {
        const seconds = (attempt - 10) * 2;
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        if (minutes > 0) {
          setStatusMessage(`⏳ Still waiting (${minutes}m ${remainingSeconds}s)...`);
        } else {
          setStatusMessage(`⏳ Still waiting (${seconds}s)...`);
        }
      }

      let result;
      
      if (isMpesa) {
        result = await pollMpesaStatus(trackingId, attempt);
      } else if (isPesapal) {
        result = await pollPesapalStatus(trackingId, attempt);
      } else {
        console.error('❌ Unknown payment type');
        setError('Unknown payment type');
        setLoading(false);
        return;
      }

      if (result.completed) {
        await handlePaymentSuccess(result.data, trackingId, false);
        isPolling = false;
        return;
      }

      if (result.failed) {
        console.log('❌ Payment FAILED');
        setPaymentStatus('FAILED');
        setStatusMessage('❌ Payment was not completed');
        setIsBackgroundPolling(false);
        setLoading(false);
        pollingActiveRef.current = false;
        setPollingComplete(true);
        
        setDonationData({
          reference: trackingId,
          amount: amount,
          status: result.data?.status || 'FAILED',
          message: result.data?.ResultDesc || result.data?.payment_status_description || 'Payment was not completed. Please try again.'
        });
        
        startRedirectCountdown('/donate');
        isPolling = false;
        return;
      }

      console.log(`⏳ Payment still PENDING (attempt ${attempt})`);
      setPaymentStatus('PENDING');

      if (attempt >= LOADING_DISPLAY_ATTEMPTS && !showPendingState) {
        setShowPendingState(true);
        setLoading(false);
        setIsBackgroundPolling(true);
      }

      if (attempt >= MAX_POLL_ATTEMPTS) {
        console.log('⏰ Polling timeout');
        setPaymentStatus('TIMEOUT');
        setStatusMessage('⏰ Payment confirmation timed out. Please check your payment status.');
        setDonationData({
          reference: trackingId,
          amount: amount,
          status: 'TIMEOUT',
          message: 'Payment confirmation timed out. Please check your payment status or contact us for assistance.'
        });
        setIsBackgroundPolling(false);
        setLoading(false);
        pollingActiveRef.current = false;
        setPollingComplete(true);
        startRedirectCountdown('/donate');
        isPolling = false;
        return;
      }

      if (isPolling) {
        pollTimeoutRef.current = setTimeout(pollStatus, POLL_INTERVAL);
      }
    };

    pollStatus();

    return () => {
      isPolling = false;
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }
    };
  }, [checkoutRequestID, reference, orderTrackingId, amount, donationData, isMpesa, isPesapal, pollingComplete, sseConnected]);

  // ============================================
  // DEBUG
  // ============================================
  useEffect(() => {
    console.log('🔍 ===== DONATION SUCCESS PAGE LOADED =====');
    console.log('📝 All Search Params:', Object.fromEntries(searchParams));
    console.log('📝 Payment Type:', isMpesa ? 'M-PESA' : isPesapal ? 'Pesapal' : 'Unknown');
    console.log('📝 orderTrackingId:', orderTrackingId);
    console.log('📝 reference:', reference);
    console.log('📝 checkoutRequestID:', checkoutRequestID);
    console.log('📝 amount:', amount);
    console.log('📝 SSE Supported:', typeof EventSource !== 'undefined');
    console.log('📝 Full URL:', window.location.href);
    console.log('============================================\n');
    
    setLoading(true);
    setPollingComplete(false);
    pollingActiveRef.current = true;
  }, []);

  // ============================================
  // RENDER STATES - LOADING
  // ============================================
  if (loading && !showPendingState) {
    return (
      <>
        <SEO 
          title="Payment Processing - Kajiado Children's Home"
          description="Verifying your donation payment. Please wait..."
          path="/donation-success"
        />
        <div className="donation-success-page">
          <div className="container">
            <div className="status-card status-loading">
              <div className="status-icon loading-icon">
                <i className="fas fa-spinner fa-spin"></i>
              </div>
              <h2 className="status-title">Processing Payment</h2>
              <p className="status-message">{statusMessage}</p>
              {isMpesa && (
                <div className="mpesa-instructions">
                  <h4>📱 What to do:</h4>
                  <ol>
                    <li>Check your phone for the M-PESA prompt</li>
                    <li>Enter your M-PESA PIN to confirm</li>
                    <li>Wait for the confirmation SMS</li>
                    <li>You'll be redirected automatically</li>
                  </ol>
                </div>
              )}
              {pollCount > 0 && (
                <div className="progress-container">
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${Math.min((pollCount / LOADING_DISPLAY_ATTEMPTS) * 100, 100)}%` }}></div>
                  </div>
                  <p className="poll-count">Checking payment... ({pollCount}s)</p>
                </div>
              )}
              {pollCount > 2 && (
                <p className="status-hint">Please don't refresh the page. We're confirming your payment.</p>
              )}
              {sseConnected && (
                <div className="sse-status">
                  <p><i className="fas fa-bolt"></i> Real-time connection active</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  // ============================================
  // RENDER STATES - ERROR
  // ============================================
  if (error) {
    return (
      <>
        <SEO 
          title="Payment Verification Failed - Kajiado Children's Home"
          description="Could not verify your donation payment. Please contact us."
          path="/donation-success"
        />
        <div className="donation-success-page">
          <div className="container">
            <div className="status-card status-error">
              <div className="status-icon error-icon">
                <i className="fas fa-exclamation-circle"></i>
              </div>
              <h2 className="status-title">Verification Failed</h2>
              <p className="status-message">{error}</p>
              <div className="status-actions">
                <Link to="/donate" className="btn-primary">
                  <i className="fas fa-redo"></i> Try Again
                </Link>
                <Link to="/contact" className="btn-secondary">
                  <i className="fas fa-envelope"></i> Contact Us
                </Link>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ============================================
  // RENDER STATES - PENDING
  // ============================================
  if (donationData?.status === 'PENDING' && isBackgroundPolling) {
    return (
      <>
        <SEO 
          title="Payment Processing - Kajiado Children's Home"
          description="Your donation is being processed. Please wait for confirmation."
          path="/donation-success"
        />
        <div className="donation-success-page">
          <div className="container">
            <div className="status-card status-pending">
              <div className="status-icon pending-icon">
                <i className="fas fa-clock"></i>
              </div>
              <h2 className="status-title">Payment Processing</h2>
              <p className="status-message">{statusMessage}</p>
              
              {isMpesa && (
                <div className="mpesa-instructions">
                  <h4>📱 What to do:</h4>
                  <ol>
                    <li>Check your phone for the M-PESA prompt</li>
                    <li>Enter your M-PESA PIN to confirm</li>
                    <li>Wait for the confirmation SMS</li>
                    <li>You'll be redirected automatically</li>
                  </ol>
                </div>
              )}
              
              <div className="background-polling-indicator">
                <div className="pulse-dot"></div>
                <span className="polling-text">
                  {sseConnected ? '📡 Real-time connection active' : `Checking for updates... (${pollCount}/${MAX_POLL_ATTEMPTS})`}
                </span>
              </div>
              
              <div className="status-details">
                <div className="detail-row">
                  <span className="detail-label">Transaction Reference</span>
                  <span className="detail-value">{donationData?.reference || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Amount</span>
                  <span className="detail-value highlight">KES {donationData?.amount || amount}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Status</span>
                  <span className="detail-value status-badge pending">⏳ PENDING</span>
                </div>
              </div>
              
              <div className="status-actions">
                <button 
                  onClick={() => window.location.reload()}
                  className="btn-primary"
                >
                  <i className="fas fa-sync"></i> Check Status Now
                </button>
                <Link to="/" className="btn-secondary">
                  <i className="fas fa-home"></i> Return Home
                </Link>
              </div>
              
              <div className="pending-note">
                <p><i className="fas fa-info-circle"></i> We're still checking for your payment confirmation. You can close this page and we'll update you via email when confirmed.</p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ============================================
  // RENDER STATES - TIMEOUT
  // ============================================
  if (donationData?.status === 'TIMEOUT') {
    return (
      <>
        <SEO 
          title="Payment Timeout - Kajiado Children's Home"
          description="Your payment confirmation has timed out. Please check your payment."
          path="/donation-success"
        />
        <div className="donation-success-page">
          <div className="container">
            <div className="status-card status-timeout">
              <div className="status-icon timeout-icon">
                <i className="fas fa-hourglass-end"></i>
              </div>
              <h2 className="status-title">Payment Timeout</h2>
              <p className="status-message">{donationData.message || 'Payment confirmation timed out. Please check your payment status.'}</p>
              <div className="status-details">
                <div className="detail-row">
                  <span className="detail-label">Transaction Reference</span>
                  <span className="detail-value">{donationData.reference}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Amount</span>
                  <span className="detail-value highlight">KES {donationData.amount}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Status</span>
                  <span className="detail-value status-badge timeout">⏰ TIMEOUT</span>
                </div>
              </div>
              <div className="status-actions">
                <Link to="/donate" className="btn-primary">
                  <i className="fas fa-redo"></i> Try Again
                </Link>
                <Link to="/contact" className="btn-secondary">
                  <i className="fas fa-envelope"></i> Contact Us
                </Link>
              </div>
              <div className="redirect-note">
                <p><i className="fas fa-clock"></i> Redirecting to donation page in {redirectCountdown} seconds...</p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ============================================
  // RENDER STATES - FAILED
  // ============================================
  if (donationData?.status === 'FAILED' || donationData?.status === 'CANCELLED') {
    return (
      <>
        <SEO 
          title="Payment Not Completed - Kajiado Children's Home"
          description="Your donation payment was not completed. Please try again."
          path="/donation-success"
        />
        <div className="donation-success-page">
          <div className="container">
            <div className="status-card status-failed">
              <div className="status-icon failed-icon">
                <i className="fas fa-times-circle"></i>
              </div>
              <h2 className="status-title">Payment Not Completed</h2>
              <p className="status-message">{donationData.message || 'Your payment was not completed. Please try again.'}</p>
              <div className="status-details">
                <div className="detail-row">
                  <span className="detail-label">Transaction Reference</span>
                  <span className="detail-value">{donationData.reference}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Amount</span>
                  <span className="detail-value highlight">KES {donationData.amount}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Status</span>
                  <span className="detail-value status-badge failed">❌ {donationData.status}</span>
                </div>
              </div>
              <div className="status-actions">
                <Link to="/donate" className="btn-primary">
                  <i className="fas fa-redo"></i> Try Again
                </Link>
                <Link to="/contact" className="btn-secondary">
                  <i className="fas fa-envelope"></i> Contact Us
                </Link>
              </div>
              <div className="redirect-note">
                <p><i className="fas fa-clock"></i> Redirecting to donation page in {redirectCountdown} seconds...</p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ============================================
  // RENDER STATES - SUCCESS
  // ============================================
  if (donationData?.status === 'COMPLETED') {
    return (
      <>
        <SEO 
          title="Donation Successful - Kajiado Children's Home"
          description="Thank you for your generous donation to Kajiado Children's Home. Your support transforms lives."
          path="/donation-success"
        />
        
        <div className="donation-success-page">
          <div className="container">
            <div className="status-card status-success">
              <div className="status-icon success-icon">
                <i className="fas fa-check-circle"></i>
              </div>
              <h2 className="status-title">🎉 Thank You for Your Donation!</h2>
              <p className="status-message">
                Your generous contribution will help us provide love, care, education, and hope to vulnerable children at Kajiado Children's Home.
              </p>
              
              {donationData && (
                <div className="status-details">
                  <div className="detail-row">
                    <span className="detail-label">Transaction Reference</span>
                    <span className="detail-value">{donationData.reference}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Amount</span>
                    <span className="detail-value highlight">KES {donationData.amount}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Status</span>
                    <span className="detail-value status-badge success">✅ COMPLETED</span>
                  </div>
                  {donationData.paymentMethod && donationData.paymentMethod !== 'N/A' && (
                    <div className="detail-row">
                      <span className="detail-label">Payment Method</span>
                      <span className="detail-value">{donationData.paymentMethod}</span>
                    </div>
                  )}
                  {donationData.confirmationCode && donationData.confirmationCode !== 'N/A' && (
                    <div className="detail-row">
                      <span className="detail-label">Confirmation Code</span>
                      <span className="detail-value">{donationData.confirmationCode}</span>
                    </div>
                  )}
                  {isLogged && (
                    <div className="detail-row success-log">
                      <span className="detail-label">📊 Data Status</span>
                      <span className="detail-value status-badge success">✅ Logged to Database</span>
                    </div>
                  )}
                  {emailSent && (
                    <div className="detail-row success-log">
                      <span className="detail-label">📧 Email Status</span>
                      <span className="detail-value status-badge success">✅ Confirmation Sent</span>
                    </div>
                  )}
                  {sseConnected && (
                    <div className="detail-row success-log">
                      <span className="detail-label">📡 Update Method</span>
                      <span className="detail-value status-badge success">✅ Real-time (SSE)</span>
                    </div>
                  )}
                  {logError && (
                    <div className="detail-row error-log">
                      <span className="detail-label">⚠️ Data Status</span>
                      <span className="detail-value status-badge warning">{logError}</span>
                    </div>
                  )}
                </div>
              )}

              <div className="status-actions">
                <Link to="/get-involved" className="btn-primary">
                  <i className="fas fa-hands-helping"></i> Get Involved
                </Link>
                <Link to="/" className="btn-secondary">
                  <i className="fas fa-home"></i> Return Home
                </Link>
              </div>

              <div className="redirect-note">
                <p><i className="fas fa-clock"></i> Redirecting to Get Involved page in {redirectCountdown} seconds...</p>
              </div>

              <div className="impact-section">
                <h3>Your Impact</h3>
                <p>Your donation will help provide:</p>
                <ul>
                  <li>🍲 Nutritious meals for children</li>
                  <li>📚 Quality education and school supplies</li>
                  <li>🏥 Medical care and healthcare support</li>
                  <li>🏠 Safe shelter and loving care</li>
                  <li>💖 Hope and a brighter future</li>
                </ul>
                <p className="blessing">
                  "The generous will themselves be blessed, for they share their food with the poor."<br />
                  <strong>Proverbs 22:9</strong>
                </p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Fallback loading
  return (
    <div className="donation-success-page">
      <div className="container">
        <div className="status-card status-loading">
          <div className="status-icon loading-icon">
            <i className="fas fa-spinner fa-spin"></i>
          </div>
          <h2 className="status-title">Loading...</h2>
          <p className="status-message">Please wait...</p>
        </div>
      </div>
    </div>
  );
}

// CSS Styles
const styles = `
  .donation-success-page {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #f0f4f8 0%, #e2e8f0 100%);
    padding: 120px 20px 60px;
  }
  
  .donation-success-page .container {
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
  }
  
  .status-card {
    max-width: 600px;
    margin: 0 auto;
    background: #ffffff;
    padding: 50px 45px;
    border-radius: 24px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.10);
    text-align: center;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
  }
  
  .status-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 6px;
  }
  
  .status-card:hover {
    box-shadow: 0 25px 70px rgba(0, 0, 0, 0.12);
    transform: translateY(-2px);
  }
  
  .status-success::before {
    background: linear-gradient(90deg, #4caf50, #66bb6a);
  }
  
  .status-success .status-icon {
    color: #4caf50;
  }
  
  .status-error::before,
  .status-failed::before {
    background: linear-gradient(90deg, #f44336, #ef5350);
  }
  
  .status-error .status-icon,
  .status-failed .status-icon {
    color: #f44336;
  }
  
  .status-pending::before,
  .status-loading::before {
    background: linear-gradient(90deg, #ff9800, #ffa726);
  }
  
  .status-pending .status-icon,
  .status-loading .status-icon {
    color: #ff9800;
  }
  
  .status-timeout::before {
    background: linear-gradient(90deg, #9e9e9e, #bdbdbd);
  }
  
  .status-timeout .status-icon {
    color: #757575;
  }
  
  .status-icon {
    font-size: 4.5rem;
    margin-bottom: 16px;
    display: block;
  }
  
  .success-icon {
    animation: successPop 0.6s ease;
  }
  
  .failed-icon,
  .error-icon {
    animation: shake 0.5s ease;
  }
  
  .pending-icon {
    animation: pulse 1.5s ease-in-out infinite;
  }
  
  .loading-icon i {
    animation: spin 1s linear infinite;
    display: inline-block;
  }
  
  .timeout-icon {
    animation: fadeIn 0.5s ease;
  }
  
  @keyframes successPop {
    0% { transform: scale(0) rotate(-20deg); opacity: 0; }
    50% { transform: scale(1.2) rotate(5deg); }
    100% { transform: scale(1) rotate(0deg); opacity: 1; }
  }
  
  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    20% { transform: translateX(-15px); }
    40% { transform: translateX(15px); }
    60% { transform: translateX(-10px); }
    80% { transform: translateX(10px); }
  }
  
  @keyframes pulse {
    0%, 100% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.1); opacity: 0.7; }
  }
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  
  @keyframes fadeIn {
    from { opacity: 0; transform: scale(0.8); }
    to { opacity: 1; transform: scale(1); }
  }
  
  .status-title {
    font-size: 2rem;
    margin-bottom: 12px;
    color: #1a202c;
    font-weight: 700;
    line-height: 1.3;
  }
  
  .status-message {
    color: #4a5568;
    line-height: 1.7;
    margin-bottom: 28px;
    font-size: 1rem;
  }
  
  .status-hint {
    color: #718096;
    font-size: 0.9rem;
    margin-top: 10px;
    font-style: italic;
  }
  
  .progress-container {
    margin: 24px 0 16px;
    width: 100%;
  }
  
  .progress-bar {
    width: 100%;
    height: 8px;
    background: #e2e8f0;
    border-radius: 4px;
    overflow: hidden;
    margin: 10px 0;
  }
  
  .progress-fill {
    height: 100%;
    background: linear-gradient(90deg, #2563eb, #4caf50);
    border-radius: 4px;
    transition: width 0.5s ease;
  }
  
  .poll-count {
    color: #718096;
    font-size: 0.85rem;
    margin: 8px 0 0;
  }
  
  .mpesa-instructions {
    background: #f0f4f8;
    padding: 16px 20px;
    border-radius: 12px;
    margin: 20px 0;
    text-align: left;
  }
  
  .mpesa-instructions h4 {
    color: #1a202c;
    margin-bottom: 8px;
  }
  
  .mpesa-instructions ol {
    margin-left: 20px;
    color: #4a5568;
    line-height: 2;
    font-size: 0.9rem;
  }
  
  .background-polling-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    margin: 16px 0 24px;
    padding: 10px;
    background: #f0f4f8;
    border-radius: 12px;
  }
  
  .pulse-dot {
    width: 12px;
    height: 12px;
    background: #4caf50;
    border-radius: 50%;
    animation: pulseDot 1.5s ease-in-out infinite;
  }
  
  @keyframes pulseDot {
    0%, 100% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.5); opacity: 0.5; }
  }
  
  .polling-text {
    color: #2d3748;
    font-weight: 500;
    font-size: 0.95rem;
  }
  
  .pending-note {
    margin-top: 16px;
    padding: 16px;
    background: #fff3e0;
    border-radius: 12px;
    text-align: left;
  }
  
  .pending-note p {
    color: #e65100;
    font-size: 0.9rem;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .pending-note p i {
    color: #ff9800;
  }
  
  .redirect-note {
    margin-top: 16px;
    padding: 12px 20px;
    background: #f0f4f8;
    border-radius: 12px;
    text-align: center;
  }
  
  .redirect-note p {
    color: #4a5568;
    font-size: 0.95rem;
    margin: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }
  
  .redirect-note p i {
    color: #2563eb;
  }
  
  .sse-status {
    margin-top: 12px;
    padding: 8px 16px;
    background: #e8f5e9;
    border-radius: 20px;
    display: inline-block;
  }
  
  .sse-status p {
    color: #2e7d32;
    font-size: 0.85rem;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  
  .sse-status p i {
    color: #4caf50;
  }
  
  .status-details {
    background: #f7fafc;
    padding: 20px 24px;
    border-radius: 14px;
    margin-bottom: 28px;
    text-align: left;
    border: 1px solid #e2e8f0;
  }
  
  .detail-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 0;
    border-bottom: 1px solid #e2e8f0;
  }
  
  .detail-row:last-child {
    border-bottom: none;
  }
  
  .detail-label {
    color: #718096;
    font-size: 0.9rem;
    font-weight: 500;
  }
  
  .detail-value {
    color: #2d3748;
    font-weight: 500;
    font-size: 0.95rem;
    text-align: right;
    word-break: break-word;
    max-width: 60%;
  }
  
  .detail-value.highlight {
    color: #2563eb;
    font-weight: 700;
    font-size: 1.05rem;
  }
  
  .detail-value .status-badge {
    display: inline-block;
    padding: 4px 14px;
    border-radius: 20px;
    font-size: 0.85rem;
    font-weight: 600;
  }
  
  .status-badge.success {
    background: #e8f5e9;
    color: #2e7d32;
  }
  
  .status-badge.failed {
    background: #ffebee;
    color: #c62828;
  }
  
  .status-badge.pending {
    background: #fff3e0;
    color: #e65100;
  }
  
  .status-badge.warning {
    background: #fff8e1;
    color: #f57f17;
  }
  
  .status-badge.timeout {
    background: #f5f5f5;
    color: #616161;
  }
  
  .detail-row.success-log {
    border-top: 2px solid #4caf50;
    margin-top: 8px;
    padding-top: 12px;
  }
  
  .detail-row.error-log {
    border-top: 2px solid #f44336;
    margin-top: 8px;
    padding-top: 12px;
  }
  
  .status-actions {
    display: flex;
    gap: 14px;
    justify-content: center;
    flex-wrap: wrap;
    margin-bottom: 30px;
  }
  
  .btn-primary {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    background: linear-gradient(135deg, #2563eb, #1d4ed8);
    color: #ffffff !important;
    padding: 14px 32px;
    border-radius: 50px;
    text-decoration: none;
    font-weight: 600;
    font-size: 0.95rem;
    transition: all 0.3s ease;
    border: 2px solid #2563eb;
    box-shadow: 0 4px 15px rgba(37, 99, 235, 0.3);
  }
  
  .btn-primary:hover {
    background: transparent;
    color: #2563eb !important;
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(37, 99, 235, 0.35);
  }
  
  .btn-secondary {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    background: transparent;
    color: #4a5568;
    padding: 14px 32px;
    border-radius: 50px;
    text-decoration: none;
    font-weight: 600;
    font-size: 0.95rem;
    transition: all 0.3s ease;
    border: 2px solid #e2e8f0;
  }
  
  .btn-secondary:hover {
    border-color: #2563eb;
    color: #2563eb;
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(37, 99, 235, 0.1);
  }
  
  .impact-section {
    padding-top: 24px;
    border-top: 2px solid #e2e8f0;
    text-align: left;
  }
  
  .impact-section h3 {
    color: #1a202c;
    margin-bottom: 12px;
    font-size: 1.2rem;
    font-weight: 600;
    text-align: center;
  }
  
  .impact-section > p {
    text-align: center;
    color: #4a5568;
    margin-bottom: 12px;
  }
  
  .impact-section ul {
    list-style: none;
    padding: 0;
    margin: 12px 0 16px;
  }
  
  .impact-section ul li {
    padding: 10px 0;
    color: #4a5568;
    font-size: 0.95rem;
    border-bottom: 1px solid #f0f4f8;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .impact-section ul li:last-child {
    border-bottom: none;
  }
  
  .blessing {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #e2e8f0;
    font-style: italic;
    color: #2d3748;
    text-align: center;
    font-size: 1rem;
  }
  
  .blessing strong {
    color: #2563eb;
  }
  
  @media (max-width: 1024px) {
    .donation-success-page {
      padding: 110px 20px 50px;
    }
  }
  
  @media (max-width: 768px) {
    .donation-success-page {
      padding: 100px 16px 40px;
      align-items: flex-start;
    }
    
    .status-card {
      padding: 32px 24px;
      border-radius: 20px;
    }
    
    .status-title {
      font-size: 1.6rem;
    }
    
    .status-icon {
      font-size: 3.5rem;
    }
    
    .status-details {
      padding: 16px;
    }
    
    .detail-row {
      flex-direction: column;
      align-items: flex-start;
      gap: 4px;
      padding: 8px 0;
    }
    
    .detail-value {
      max-width: 100%;
      text-align: left;
    }
    
    .status-actions {
      flex-direction: column;
      gap: 12px;
    }
    
    .btn-primary,
    .btn-secondary {
      width: 100%;
      justify-content: center;
      padding: 12px 24px;
    }
    
    .impact-section ul li {
      font-size: 0.9rem;
      padding: 8px 0;
    }
  }
  
  @media (max-width: 480px) {
    .donation-success-page {
      padding: 90px 12px 30px;
    }
    
    .status-card {
      padding: 24px 16px;
    }
    
    .status-title {
      font-size: 1.3rem;
    }
    
    .status-message {
      font-size: 0.9rem;
    }
    
    .status-icon {
      font-size: 3rem;
    }
    
    .progress-container {
      margin: 16px 0;
    }
    
    .status-details {
      padding: 12px 14px;
    }
    
    .detail-label {
      font-size: 0.8rem;
    }
    
    .detail-value {
      font-size: 0.85rem;
    }
    
    .detail-value.highlight {
      font-size: 0.95rem;
    }
    
    .impact-section h3 {
      font-size: 1rem;
    }
    
    .impact-section ul li {
      font-size: 0.85rem;
      padding: 6px 0;
    }
    
    .blessing {
      font-size: 0.9rem;
    }
  }
`;

// Apply styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
}